/**
 * Snapshot visibility normalisation.
 *
 * `_visibility` is documented (constants/visibility.ts) as a TRANSIENT
 * hint — only VISIBLE (1.0) and DIMMED (0.55) are valid, and DIMMED is
 * meant for in-session signals (Follow the Money context dim, SSE
 * discovery flicker). It must not survive across save/load.
 *
 * Without this, a user who saves an investigation while FTM is active
 * (or while SSE was mid-stream) re-opens the snapshot with 50%+ of
 * nodes faded to 55% opacity — they read as inactive/hanging even
 * when they have edges. Confirmed in production snapshot
 * 73197ddc-7106-45a4-bd6f-c575eb08df82 (5/4/2026): 23 of 26 nodes
 * persisted at _visibility=0.55 including the root, because the user
 * saved while FTM mode had dimmed the non-path neighbourhood.
 *
 * We normalise on BOTH ends:
 *   - SERIALIZE: strip transient dim before writing to graph_state,
 *     so future loads start clean.
 *   - LOAD: also normalise on read, so existing broken snapshots
 *     (saved before this fix) display correctly.
 */
import { VISIBILITY } from '../constants/visibility';

interface SerializedElement {
    data?: { _visibility?: number;[k: string]: any };
    [k: string]: any;
}

interface CytoscapeJsonElements {
    nodes?: SerializedElement[];
    edges?: SerializedElement[];
}

/**
 * Lift any transient _visibility back to VISIBLE on the serialized
 * clone. Called by the save path after `cy.json()` so the live canvas
 * is not mutated and the user sees no visual flash. In place; the
 * input shape mirrors Cytoscape's own JSON export.
 */
export function normalizeVisibilityForSerialize(elements: CytoscapeJsonElements | undefined): void {
    if (!elements) return;
    const lift = (el: SerializedElement | undefined) => {
        if (el?.data && el.data._visibility !== undefined && el.data._visibility !== VISIBILITY.VISIBLE) {
            el.data._visibility = VISIBILITY.VISIBLE;
        }
    };
    elements.nodes?.forEach(lift);
    elements.edges?.forEach(lift);
}

/**
 * Return a normalised copy of a flat elements array (the load path
 * collapses Cytoscape's {nodes, edges} into one list). Returns the
 * input array unchanged when no element needs normalising — keeps
 * referential equality in the common case.
 */
export function normalizeVisibilityOnLoad<T extends SerializedElement>(elements: T[]): T[] {
    let touched = false;
    const next = elements.map(el => {
        if (el?.data && el.data._visibility !== undefined && el.data._visibility !== VISIBILITY.VISIBLE) {
            touched = true;
            return { ...el, data: { ...el.data, _visibility: VISIBILITY.VISIBLE } };
        }
        return el;
    });
    return touched ? next : elements;
}
